import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Trophy } from 'lucide-react';
import { championshipApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { PublicAthleteList } from '../components/PublicAthleteList';

/**
 * Dedicated public page for the championship's registered athletes.
 * Reached from the "Atletas Inscritos" button on the public championship
 * landing. Lightweight: just a header (with championship title + back link)
 * and the full PublicAthleteList component.
 */
export default function PublicAthletesPage() {
  const { id } = useParams();
  const [championship, setChampionship] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await championshipApi.get(id);
        setChampionship(data);
      } catch (e) {
        setChampionship(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
      </div>
    );
  }

  if (!championship) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#A1A1AA]">
        Campeonato nao encontrado
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="public-athletes-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={`/public/${id}`}
              className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#D4AF37] transition-colors"
              data-testid="back-to-championship"
              title="Voltar ao campeonato"
            >
              <ArrowLeft size={16} />
            </Link>
            <div className="min-w-0">
              <p className="text-xs text-[#71717A] uppercase tracking-wider">Atletas Inscritos</p>
              <h1 className="text-base sm:text-lg font-bold text-white font-['Chivo'] truncate">
                {championship.name}
              </h1>
            </div>
          </div>
          <Link to={`/public/${id}`}>
            <Button variant="outline" size="sm" className="border-[#27272A] text-[#A1A1AA] hover:text-white" data-testid="back-button">
              <Trophy size={16} className="mr-1.5" /> Pagina do Campeonato
            </Button>
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="py-8">
        <PublicAthleteList championshipId={id} />
      </main>
    </div>
  );
}
